import React, { useRef, useEffect } from "react";
import cx from "classnames";
import styled from "styled-components";
import { Copy } from "components/components/atoms";
import breakpoints from "utils/breakpoints";
import getEmbedUrl from "utils/getEmbedUrl";
import useBreakpoint from "utils/hooks/useBreakpoint";
import { AspectRatio } from "react-aspect-ratio";
import {
  videoTranscriptFileUrl,
  videoTranscriptFileExtension,
} from "utils/videoTranscriptFileUrl";
import { isValidUrl } from "utils/validateUrlSrc";
import { isValidTitle } from "utils/validateTitle";
import { getMobileOS } from "utils/getMobileOS";
import LazyLoad from "react-lazyload";

const Iframe = styled.iframe`
  @media ${breakpoints.md} {
    height: 24.375rem;
  }
  @media ${breakpoints.lg} {
    height: 33.125rem;
  }
  @media ${breakpoints.xl} {
    height: 40.6rem;
  }
`;

const TextSlideContainer = styled.div`
  min-height: 18rem;
`;
const SectionHeading = styled.h2`
  letter-spacing: -0.01em;
`;

const TimelineSlide = ({
  index,
  position,
  labelName = "",
  title = "",
  text = "",
  image = {},
  video = {},
  transcript = {},
  transcriptText = "",
}) => {
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);
  const isIOS = getMobileOS() === "iOS";
  //const ratio = isMobile ? "330/300" : "640/500";
  const ratio = isMobile ? "330/300" : "640/500";
  const showVideo = video && video.href && video.href.length > 10;
  const videoRef = useRef(null);

  useEffect(() => {
    if (index !== position && videoRef && videoRef.current) {
      setTimeout(() => {
        videoRef.current.pause();
      });
    }
  }, [index, position, videoRef]);

  return (
    <div
      className={cx("flex mb-8 md:mb-12 ", {
        "flex-col px-2": isMobile,
        "flex-row": !isMobile,
      })}
    >
      <TextSlideContainer
        className={cx("mx-4 self-center", {
          "w-full": isMobile,
          "md:w-1/2 mr-16": !isMobile,
        })}
      >
        <SectionHeading className="pt-2 font-bold font-trade-gothic-20 text-2xl md:text-4xl mb-3 text-mattamy-blue leading-tiny">
          {title}
        </SectionHeading>

        <Copy
          color="blue"
          className="pb-10 md:pb-4 leading-relaxed text-sm md:text-normal break-normal"
        >
          {text}
        </Copy>
      </TextSlideContainer>
      <div
        className={cx("", {
          "w-full": isMobile,
          "md:w-1/2": !isMobile,
        })}
      >
        <AspectRatio ratio={ratio}>
          <div className="w-full justify-center text-center bg-mattamy-gray-bg rounded">
            {!showVideo && (
              <img
                src={image && image.src}
                alt={image && image.alt}
                className="h-full min-w-full object-cover rounded bg-mattamy-gray-bg"
              />
            )}

            {showVideo && video.type === "external" && (
              <div
                className={cx(
                  "videoIframe w-full h-full rounded bg-mattamy-gray-bg",
                  {}
                )}
              >
                <Iframe
                  className={"h-full w-full rounded"}
                  src={getEmbedUrl(video.href, false, true)}
                  title={transcriptText}
                  aria-label={transcriptText}
                />
              </div>
            )}
            {showVideo && video.type === "internal" && (
              <LazyLoad className="w-full h-full rounded">
                <video
                  src={video.href + (isIOS ? "#t=0.001" : "")}
                  ref={videoRef}
                  className="h-full w-full rounded bg-black"
                  controls
                  disablepictureinpicture="true"
                  controlslist="nodownload"
                />
              </LazyLoad>
            )}
          </div>
        </AspectRatio>
        {showVideo &&
          transcriptText &&
          transcript &&
          transcript.link &&
          transcript.link.href && (
            <div className="text-left pb-10 md:pb-4 leading-relaxed text-sm md:text-normal font-graphie">
              <span className="text-left text-mattamy-blue pb-10 md:pb-4 leading-relaxed text-sm md:text-normal ">
                {"Transcript:"}
              </span>
              <span className="text-left ml-2 text-mattamy-blue  pb-10 md:pb-4 leading-relaxed text-sm md:text-normal underline">
                <a
                  href={videoTranscriptFileUrl(transcript)}
                  download={
                    transcript.name +
                    videoTranscriptFileExtension(transcript.extension)
                  }
                  aria-label={"Download " + transcriptText + " transcript"}
                >
                  {transcriptText}
                </a>
              </span>
            </div>
          )}
      </div>
    </div>
  );
};

export default TimelineSlide;
